import {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    removeNotification,
    setMessage,
    setWorkspaceMembership,
    upsertNotification,
} from "../app-slice";
import {
    isRemovedRealtimeEvent,
    getRealtimeEventDescriptor,
    patchWorkspaceMembershipData,
} from "./workspace-realtime-utils";
import {useRealtime} from "./useRealtime";
import {useTask} from "../../models/task/use-task";
import {useProject} from "../../models/project/use-project";
import {useGoal} from "../../models/goal/use-goal";
import {useDeliverable} from "../../models/deliverable/use-deliverable";
import {useTeam} from "../../models/team/use-team";
import useWorkspaceMembership from "../../models/workspace-membership/use-workspace-membership";
import {useChat} from "../../models/chat/use-chat";
import {useChatMessage} from "../../models/chat-message/use-chat-message";
import {useChatParticipant} from "../../models/chat-participant/use-chat-participant";
import {
    removeWorkspaceChat,
    removeWorkspaceChatMessage,
    removeWorkspaceChatParticipant,
    removeWorkspaceInboxItem,
    removeWorkspaceGoal,
    removeWorkspaceProject,
    removeWorkspaceTask,
    removeWorkspaceTeam,
    mergeWorkspaceTasks,
    upsertWorkspaceGoal,
    upsertWorkspaceProject,
    upsertWorkspaceTask,
    upsertWorkspaceTeam,
    upsertWorkspaceChat,
    upsertWorkspaceChatMessage,
    upsertWorkspaceChatParticipant,
    upsertWorkspaceInboxItem,
    upsertWorkspaceUserRole,
    removeWorkspaceUserRole,
} from "../workspace-graph-slice";
import {getItemKey} from "./workspace-realtime-utils";
import {normalizeMembershipEntity} from "../../models/workspace-membership/workspace-membership-identity";
import {playNotificationSound} from "../notification-sound";
import {showLocalNotification} from "../push/push-client";

function isCreatedRealtimeEvent(event) {
    const type = String(event?.type ?? event?.event ?? event?.action ?? "").toLowerCase();
    return type.includes("created") || type === "create" || type.endsWith(".create");
}

function sameEntityIdentity(left, right) {
    const leftKeys = [left?.documentId, left?.id]
        .filter((value) => value !== null && value !== undefined)
        .map(String);
    return [right?.documentId, right?.id]
        .filter((value) => value !== null && value !== undefined)
        .map(String)
        .some((key) => leftKeys.includes(key));
}

function messageToastContent(message) {
    const messageUser = message?.user?.member;
    const sender = messageUser?.fullname
        || [messageUser?.firstname, messageUser?.lastname].filter(Boolean).join(" ")
        || messageUser?.username
        || "یکی از اعضا";
    const group = message?.chat?.type === "group" && message?.chat?.title
        ? ` در گروه «${message.chat.title}»`
        : "";
    const preview = String(
        message?.messageContent
        || message?.attachment?.[0]?.name
        || "پیام جدید"
    ).replace(/\s+/g, " ").trim().slice(0, 100);

    return `پیام جدید${group} از ${sender}: ${preview}`;
}

function useWorkspaceRealtimeSync(enabled = true) {
    const dispatch = useDispatch();
    const realTime = useRealtime();
    const workspaceMembershipData = useSelector((state) => state.app.workspaceMembershipData);
    const currentUser = useSelector((state) => state.auth.user);
    const taskApi = useTask();
    const projectApi = useProject();
    const goalApi = useGoal();
    const deliverableApi = useDeliverable();
    const teamApi = useTeam();
    const workspaceMembershipApi = useWorkspaceMembership();
    const chatApi = useChat();
    const chatMessageApi = useChatMessage();
    const chatParticipantApi = useChatParticipant();
    const latestRef = useRef({});
    const queuedSequenceRef = useRef(0);
    const fallbackEventRef = useRef(null);
    const processingRef = useRef(Promise.resolve());
    const toastedMessageKeysRef = useRef(new Set());

    latestRef.current = {
        workspaceMembershipData,
        currentUser,
        taskApi,
        projectApi,
        goalApi,
        deliverableApi,
        teamApi,
        workspaceMembershipApi,
        chatApi,
        chatMessageApi,
        chatParticipantApi,
    };

    const syncRealtimeEvent = async (event) => {
        const descriptor = getRealtimeEventDescriptor(event);
        if (!descriptor.kind) return;

        const current = latestRef.current;
        const currentWorkspace = current.workspaceMembershipData?.workspace;
        const eventWorkspaceId = event?.__realtimeWorkspaceId;
        const belongsToCurrentWorkspace = [currentWorkspace?.id, currentWorkspace?.documentId]
            .filter((value) => value !== null && value !== undefined)
            .some((value) => String(value) === String(eventWorkspaceId));

        if (
            eventWorkspaceId !== null
            && eventWorkspaceId !== undefined
            && !belongsToCurrentWorkspace
            && descriptor.kind !== "userEventLog"
        ) {
            return;
        }

        if (descriptor.kind === "workspaceMembership" || descriptor.kind === "workspace") {
            await current.workspaceMembershipApi.getWorkspaceMemberships();
            return;
        }

        if (descriptor.kind === "userRole") {
            const role = descriptor.payload && typeof descriptor.payload === "object"
                ? {...descriptor.payload, documentId: descriptor.payload.documentId ?? descriptor.documentId}
                : null;
            if (!role) return;

            const roleBelongsToWorkspace = sameEntityIdentity(role.workspace, currentWorkspace);
            const roleMembership = role.workspace_membership;
            const roleBelongsToCurrentMembership = sameEntityIdentity(
                roleMembership,
                current.workspaceMembershipData,
            ) || sameEntityIdentity(
                roleMembership?.member,
                current.workspaceMembershipData?.member,
            );

            // Direct user events may arrive for another workspace of the same user.
            // Only the active workspace graph is allowed to consume this role.
            if (!roleBelongsToWorkspace || !roleBelongsToCurrentMembership) return;

            const remove = isRemovedRealtimeEvent(event, role);
            dispatch(remove ? removeWorkspaceUserRole(role) : upsertWorkspaceUserRole(role));

            const nextWorkspaceMembershipData = patchWorkspaceMembershipData(
                current.workspaceMembershipData,
                descriptor.kind,
                role,
                remove,
            );
            if (nextWorkspaceMembershipData !== current.workspaceMembershipData) {
                dispatch(setWorkspaceMembership(nextWorkspaceMembershipData));
            }
            return;
        }

        if (!descriptor.documentId) return;

        // Deleted entities must not be fetched again. Besides wasting a
        // request, the global API interceptor displays the expected 404
        // as a user-facing NotFound error before the model hook can catch it.
        const removeEvent = isRemovedRealtimeEvent(event, null);
        let entity = removeEvent ? descriptor.payload : null;

        if (removeEvent) {
            entity = entity && typeof entity === "object"
                ? {...entity, documentId: entity.documentId ?? descriptor.documentId}
                : {documentId: descriptor.documentId, id: descriptor.documentId};
        } else if (descriptor.kind === "task") {
            entity = await current.taskApi.getTask(descriptor.documentId);
        } else if (descriptor.kind === "project") {
            entity = await current.projectApi.getProject(descriptor.documentId);
        } else if (descriptor.kind === "goal") {
            entity = await current.goalApi.getGoal(descriptor.documentId);
        } else if (descriptor.kind === "deliverable") {
            entity = await current.deliverableApi.getDeliverable(descriptor.documentId);
        } else if (descriptor.kind === "team") {
            entity = await current.teamApi.getTeam(descriptor.documentId);
        } else if (descriptor.kind === "chat") {
            entity = await current.chatApi.getChat(descriptor.documentId);
        } else if (descriptor.kind === "chatMessage") {
            entity = await current.chatMessageApi.getChatMessage(descriptor.documentId);
        } else if (descriptor.kind === "chatParticipant") {
            // Participant presence is broadcast to every member of the chat.
            // Other participants are intentionally not readable through the
            // self-scoped REST collection, so consume the trusted populated
            // private-socket payload instead of issuing a guaranteed 404.
            entity = descriptor.payload && typeof descriptor.payload === "object"
                ? {
                    ...descriptor.payload,
                    documentId: descriptor.payload.documentId ?? descriptor.documentId,
                }
                : null;
            entity = normalizeMembershipEntity(
                entity,
                "chatParticipant",
                current.workspaceMembershipData?.workspace?.workspace_memberships || [],
            );

            const participantWorkspace = entity?.workspace;
            const belongsToCurrentWorkspace = [participantWorkspace?.id, participantWorkspace?.documentId]
                .filter((value) => value !== null && value !== undefined)
                .some((participantWorkspaceKey) => (
                    [currentWorkspace?.id, currentWorkspace?.documentId]
                        .filter((value) => value !== null && value !== undefined)
                        .some((currentWorkspaceKey) => String(currentWorkspaceKey) === String(participantWorkspaceKey))
                ));
            if (!belongsToCurrentWorkspace) return;
        } else if (descriptor.kind === "userEventLog") {
            // User event logs are pushed directly to their owner with the full,
            // populated record. Re-fetching here can race the create transaction
            // and briefly return 404 before the database commit is visible.
            entity = descriptor.payload && typeof descriptor.payload === "object"
                ? {
                    ...descriptor.payload,
                    documentId: descriptor.payload.documentId ?? descriptor.documentId,
                }
                : null;
            entity = normalizeMembershipEntity(
                entity,
                "userEventLog",
                current.workspaceMembershipData?.workspace?.workspace_memberships || [],
            );

            const ownerId = entity?.owner?.id ?? entity?.owner;
            if (
                ownerId !== null
                && ownerId !== undefined
                && current.currentUser?.id !== null
                && current.currentUser?.id !== undefined
                && String(ownerId) !== String(current.currentUser.id)
            ) {
                return;
            }
        }

        const remove = removeEvent || isRemovedRealtimeEvent(event, entity);

        if (!entity && !remove) return;

        if (
            descriptor.kind === "chatMessage"
            && !remove
            && isCreatedRealtimeEvent(event)
            && String(entity?.user?.documentId || entity?.user?.id)
                !== String(current.workspaceMembershipData?.documentId || current.workspaceMembershipData?.id)
        ) {
            const toastKey = String(entity?.documentId ?? entity?.id ?? descriptor.documentId);
            if (toastKey && !toastedMessageKeysRef.current.has(toastKey)) {
                toastedMessageKeysRef.current.add(toastKey);
                if (toastedMessageKeysRef.current.size > 200) {
                    const oldestKey = toastedMessageKeysRef.current.values().next().value;
                    toastedMessageKeysRef.current.delete(oldestKey);
                }
                dispatch(setMessage({severity: "info", content: messageToastContent(entity)}));
                playNotificationSound(`chat-message:${toastKey}`);
            }
        }

        const entityToStore = entity ?? {documentId: descriptor.documentId, id: descriptor.documentId};

        if (descriptor.kind === "task") {
            dispatch(remove ? removeWorkspaceTask(entityToStore) : upsertWorkspaceTask(entityToStore));
        } else if (descriptor.kind === "chat") {
            dispatch(remove ? removeWorkspaceChat(entityToStore) : upsertWorkspaceChat(entityToStore));
            return;
        } else if (descriptor.kind === "chatMessage") {
            dispatch(remove ? removeWorkspaceChatMessage(entityToStore) : upsertWorkspaceChatMessage(entityToStore));
            return;
        } else if (descriptor.kind === "chatParticipant") {
            dispatch(remove ? removeWorkspaceChatParticipant(entityToStore) : upsertWorkspaceChatParticipant(entityToStore));
            return;
        } else if (descriptor.kind === "userEventLog") {
            const eventWorkspace = entityToStore?.workspace;
            // personal notifications (no workspace) belong to every workspace
            const eventBelongsToCurrentWorkspace = !eventWorkspace || [eventWorkspace?.id, eventWorkspace?.documentId]
                .filter((value) => value !== null && value !== undefined)
                .some((eventWorkspaceKey) => (
                    [currentWorkspace?.id, currentWorkspace?.documentId]
                        .filter((value) => value !== null && value !== undefined)
                        .some((currentWorkspaceKey) => String(currentWorkspaceKey) === String(eventWorkspaceKey))
                ));

            if (remove || !eventBelongsToCurrentWorkspace) {
                dispatch(removeWorkspaceInboxItem(entityToStore));
            } else {
                dispatch(upsertWorkspaceInboxItem(entityToStore));
            }
            if (!remove && isCreatedRealtimeEvent(event) && !entityToStore.isRead) {
                // open-tab fallback for browser push (same tag as the server push, never shown twice); like a push it
                // can come from any of the user's workspaces and a click switches to that workspace
                showLocalNotification(entityToStore);
            }
            // inside the app, workspaces are isolated: sound and toast only for the active workspace's notifications
            // (and account-level ones without a workspace)
            if (!remove && isCreatedRealtimeEvent(event) && !entityToStore.isRead && eventBelongsToCurrentWorkspace) {
                playNotificationSound(`notification:${getItemKey(entityToStore)}`);
                // the user is looking at Zofino: no system popup, show it in the app instead
                if (entityToStore.delivery?.showNow && document.visibilityState === "visible" && document.hasFocus()) {
                    const push = entityToStore.delivery.push;
                    dispatch(setMessage({severity: "info", content: push ? `${push.title} — ${push.body}` : entityToStore.title}));
                }
            }
            dispatch(remove ? removeNotification(entityToStore) : upsertNotification(entityToStore));
            return;
        } else if (descriptor.kind === "project") {
            dispatch(remove ? removeWorkspaceProject(entityToStore) : upsertWorkspaceProject(entityToStore));
            if (!remove && Array.isArray(entityToStore?.tasks)) {
                dispatch(mergeWorkspaceTasks(entityToStore.tasks));
            }
        } else if (descriptor.kind === "goal") {
            dispatch(remove ? removeWorkspaceGoal(entityToStore) : upsertWorkspaceGoal(entityToStore));
        } else if (descriptor.kind === "deliverable") {
            const workspace = current.workspaceMembershipData?.workspace;
            const deliverableKey = getItemKey(entityToStore);
            const projectWithDeliverable = workspace?.projects?.find((project) => (
                Array.isArray(project?.deliverables) &&
                project.deliverables.some((deliverable) => getItemKey(deliverable) === deliverableKey)
            ));
            const projectKey = getItemKey(entityToStore?.project) || getItemKey(projectWithDeliverable);
            const nextWorkspaceMembershipData = patchWorkspaceMembershipData(
                current.workspaceMembershipData,
                descriptor.kind,
                entityToStore,
                remove
            );

            if (nextWorkspaceMembershipData !== current.workspaceMembershipData) {
                dispatch(setWorkspaceMembership(nextWorkspaceMembershipData));

                const updatedProject = nextWorkspaceMembershipData.workspace?.projects?.find((project) => (
                    projectKey
                        ? getItemKey(project) === projectKey
                        : Array.isArray(project?.deliverables) && project.deliverables.some((deliverable) => getItemKey(deliverable) === deliverableKey)
                ));

                if (updatedProject) dispatch(upsertWorkspaceProject(updatedProject));
            }

            return;
        } else if (descriptor.kind === "team") {
            dispatch(remove ? removeWorkspaceTeam(entityToStore) : upsertWorkspaceTeam(entityToStore));
        }

        const nextWorkspaceMembershipData = patchWorkspaceMembershipData(
            current.workspaceMembershipData,
            descriptor.kind,
            entityToStore,
            remove
        );

        if (nextWorkspaceMembershipData !== current.workspaceMembershipData) {
            dispatch(setWorkspaceMembership(nextWorkspaceMembershipData));
        }
    };

    useEffect(() => {
        if (!enabled) return;

        const queuedEvents = Array.isArray(realTime.eventQueue) ? realTime.eventQueue : [];
        const pendingEvents = queuedEvents
            .filter((event) => Number(event?.__realtimeSequence) > queuedSequenceRef.current)
            .sort((left, right) => Number(left.__realtimeSequence) - Number(right.__realtimeSequence));

        if (pendingEvents.length) {
            pendingEvents.forEach((event) => {
                queuedSequenceRef.current = Math.max(
                    queuedSequenceRef.current,
                    Number(event.__realtimeSequence)
                );
                processingRef.current = processingRef.current
                    .then(() => syncRealtimeEvent(event))
                    .catch((error) => {
                        console.error("[useWorkspaceRealtimeSync]", error);
                    });
            });
            return;
        }

        // Backwards compatibility for a custom provider that only exposes
        // lastEvent and does not stamp events with a sequence number.
        if (
            realTime.lastEvent
            && !realTime.lastEvent.__realtimeSequence
            && fallbackEventRef.current !== realTime.lastEvent
        ) {
            fallbackEventRef.current = realTime.lastEvent;
            processingRef.current = processingRef.current
                .then(() => syncRealtimeEvent(realTime.lastEvent))
                .catch((error) => {
                    console.error("[useWorkspaceRealtimeSync]", error);
                });
        }
        // syncRealtimeEvent reads APIs and workspace data from latestRef.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, enabled, realTime.eventQueue, realTime.lastEvent]);
}

export default useWorkspaceRealtimeSync;
